/**
 * LeftSideDrawer Component
 *
 * Left overlay drawer that sits on top of parent content without affecting layout.
 * Manages its own open/close state and renders children in the drawer body.
 *
 * Used by:
 * - MonsterPlacementPhase (wraps MonsterList)
 * - Any phase that needs a left-side panel (e.g. GameplayPhase)
 */

import React, { useState } from 'react';
import './SideDrawer.css';

export interface LeftSideDrawerProps {
    /** Title shown in the drawer header */
    title: string;
    /** Content rendered inside the scrollable drawer body */
    children: React.ReactNode;
    /** Whether the drawer is open by default */
    defaultOpen?: boolean;
}

export const LeftSideDrawer: React.FC<LeftSideDrawerProps> = ({
    title,
    children,
    defaultOpen = true,
}) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <>
            <div
                className={`side_drawer ${!open ? 'side_drawer--closed' : ''}`}
                aria-hidden={!open}
            >
                <div className="side_drawer_header">
                    <h2 className="side_drawer_title">{title}</h2>
                    <button
                        type="button"
                        className="side_drawer_close"
                        onClick={() => setOpen(false)}
                        aria-label="Close drawer"
                    >
                        ×
                    </button>
                </div>
                <div className="side_drawer_body">
                    {children}
                </div>
            </div>

            {!open && (
                <button
                    type="button"
                    className="side_drawer_toggle"
                    onClick={() => setOpen(true)}
                    aria-label={`Open ${title}`}
                    title={title}
                >
                    ☰
                </button>
            )}
        </>
    );
};

export default LeftSideDrawer;
